import { module } from 'modujs';
import { Blossom } from '@blossom-carousel/core';
import { $screenDebounce } from '../stores/screen';

export default class extends module {

    constructor(m) {
        super(m);

        // UI
        this.$container = this.$('container')[0];
        this.$items = this.$('item');
        this.$prev = this.$('prev')[0];
        this.$next = this.$('next')[0];
        this.$pagination = this.$('pagination')[0];
        this.$pageClone = this.$pagination?.querySelector('[data-page-clone]');
        this.$pages = this.$pagination?.querySelectorAll('[data-page]');

        this.$links = this.$('link');

        // Data
        this.totalPages = -1; // Total number of pages
        this.oldActiveIndex = -1; // Previous active index
        this.activeIndex = 0;
        this.threshold = 0.65; // Threshold for active index detection
        this.itemWidth = 0;
    }

    // =============================================================================
    // Lifecycle
    // =============================================================================
    init() {
        if (!this.$container || !this.$items.length) {
            console.warn('Carousel: Container or items not found.');
            return;
        }

        this.$links.forEach(($link) => {
            $link.setAttribute('draggable', 'false');
        });

        this.blossom = Blossom(this.$container);
        this.blossom.init();

        this.computeMetrics();
        this.computePagination();

        requestAnimationFrame(() => {
            this.bindEvents();
            this.onScroll();
        });
    }

    destroy() {
        super.destroy();
        this.blossom.destroy();
        this.unbindEvents();
    }

    // =============================================================================
    // Events
    // =============================================================================
    bindEvents() {
        this.unbindScreenListener = $screenDebounce.subscribe(this.onResize);
        this.$container.addEventListener('scroll', this.onScroll);
        this.$prev?.addEventListener('click', this.onPrevClick);
        this.$next?.addEventListener('click', this.onNextClick);
    }

    unbindEvents() {
        this.unbindScreenListener?.();
        this.$container.removeEventListener('scroll', this.onScroll);
        this.$prev?.removeEventListener('click', this.onPrevClick);
        this.$next?.removeEventListener('click', this.onNextClick);
    }

    // =============================================================================
    // Callbacks
    // =============================================================================
    onPrevClick = () => {
        this.goTo(this.activeIndex - 1);
    };

    onNextClick = () => {
        this.goTo(this.activeIndex + 1);
    };

    onPageClick = (event) => {
        const $target = event.currentTarget;
        const parent = $target.parentNode;
        const index = parseInt(parent?.dataset.index || '0', 10);

        if (index === this.activeIndex) return;

        this.goTo(index);
    };

    onSlideChange = (index) => {
        // Update old active index
        this.oldActiveIndex = this.activeIndex;

        // Update active index
        this.$pages.forEach(($page) => {
            $page.classList.remove('is-active');
        });

        if (this.$pages[index]) {
            this.$pages[index].classList.add('is-active');
        }

        // Update active index
        this.$items.forEach(($item) => {
            $item.classList.remove('is-active');
            $item.classList.remove('is-prev');
            $item.classList.remove('is-next');
        });

        if (this.$items[index]) {
            this.$items[index].classList.add('is-active');
        }

        if (this.$items[index - 1]) {
            this.$items[index - 1].classList.add('is-prev');
        }

        if (this.$items[index + 1]) {
            this.$items[index + 1].classList.add('is-next');
        }

        // Disable next button if on last slide
        if (index == this.$pages.length - 1) {
            this.$next.disabled = true;
        } else {
            this.$next.disabled = false;
        }

        // Disable previous button if on first slide
        if (index == 0) {
            this.$prev.disabled = true;
        } else {
            this.$prev.disabled = false;
        }
    };

    onResize = () => {
        this.computeMetrics();
        this.computePagination();
        this.onScroll();
    };

    onScroll = () => {
        const scrollLeft = Math.abs(this.$container.scrollLeft);
        const scrollProgress = (scrollLeft - this.threshold * this.itemWidth) / this.containerWidth;
        this.activeIndex = Math.floor(scrollProgress / (1 / this.$items.length) + 1);

        // Check if the active index has changed
        if (this.activeIndex !== this.oldActiveIndex) {
            this.onSlideChange(this.activeIndex);
        }
    };

    // =============================================================================
    // Methods
    // =============================================================================

    computeMetrics() {
        this.itemWidth = this.$items[0]?.getBoundingClientRect().width || 0;
        this.containerWidth = this.$items.length * this.itemWidth;
    }

    computePagination() {
        // Calculate the items per view
        this.itemsPerView = Math.floor(
            this.$container.getBoundingClientRect().width / this.itemWidth
        );

        // Calculate how many indexes we need
        const totalPages = this.$items.length - this.itemsPerView + 1;

        if (totalPages !== this.totalPages) {
            this.removePaginationEvents();

            this.totalPages = totalPages;

            // Clear previous pagination
            this.$pagination.innerHTML = '';

            if (this.totalPages > 1) {
                this.showPagination();
            } else {
                this.hidePagination();
                return;
            }

            // Add pagination items
            for (let i = 0; i < this.totalPages; i++) {
                const $page = this.$pageClone.cloneNode(true);
                $page.removeAttribute('data-page-clone');
                $page.dataset.index = i.toString();
                this.$pagination.appendChild($page);
            }

            this.$pageClone.remove();

            // Update reference
            this.$pages = this.$pagination.querySelectorAll('[data-page]');
            this.addPaginationEvents();
        }
    }

    showPagination() {
        this.el.classList.remove('is-pagination-hidden');
    }

    hidePagination() {
        this.el.classList.add('is-pagination-hidden');
    }

    removePaginationEvents() {
        this.$pages?.forEach(($page) => {
            $page.removeEventListener('click', this.onPageClick);
        });
    }

    addPaginationEvents() {
        this.$pages.forEach(($page) => {
            $page.addEventListener('click', this.onPageClick);
        });
    }

    goTo(index) {
        if (index < 0 || index >= this.$pages.length) {
            console.warn('Carousel: Index out of bounds.');
            return;
        }

        this.$items[index].scrollIntoView({
            behavior: 'smooth',
            inline: 'start',
            block: 'nearest'
        });

        this.activeIndex = index;
        this.onSlideChange(this.activeIndex);
    }
}
